import DeleteFilterTabDialog from "@dashboard/components/DeleteFilterTabDialog";
import SaveFilterTabDialog from "@dashboard/components/SaveFilterTabDialog";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { useRowSelection } from "@dashboard/hooks/useRowSelection";
import { ListViews } from "@dashboard/types";
import createDialogActionHandlers from "@dashboard/utils/handlers/dialogActionHandlers";
import createSortHandler from "@dashboard/utils/handlers/sortHandler";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { getSortParams } from "@dashboard/utils/sort";
import isEqual from "lodash/isEqual";
import React, { useCallback } from "react";
import { useIntl } from "react-intl";

import ParentAttributeBulkDeleteDialog from "../../components/ParentAttributeBulkDeleteDialog";
import ParentAttributeListPage from "../../components/ParentAttributeListPage";
import {
  parentAttributeListUrl,
  ParentAttributeListUrlDialog,
  ParentAttributeListUrlQueryParams,
} from "../../urls";
import { useGetParentAttributesQuery, useParentAttributeBulkDeleteMutation } from "@dashboard/graphql";
import { getSortQueryVariables } from "./sort";
import { storageUtils, getFilterOpts, getFilterQueryParam } from "./filters";
import { useFilterPresets } from "@dashboard/hooks/useFilterPresets";
import createFilterHandlers from "@dashboard/utils/handlers/filterHandlers";

interface ParentAttributeListProps {
  params: ParentAttributeListUrlQueryParams;
}

const ParentAttributeList = ({ params }: ParentAttributeListProps) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const { updateListSettings, settings } = useListSettings(ListViews.ATTRIBUTE_LIST);

  usePaginationReset(parentAttributeListUrl, params, settings.rowNumber);

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
    }),
    [params, settings.rowNumber],
  );

  const { data, loading, refetch } = useGetParentAttributesQuery({
    variables: queryVariables,
  });

  const {
    clearRowSelection,
    selectedRowIds,
    setSelectedRowIds,
    setClearDatagridRowSelectionCallback,
  } = useRowSelection(params);

  const {
    hasPresetsChanged,
    onPresetChange,
    onPresetDelete,
    onPresetSave,
    onPresetUpdate,
    selectedPreset,
    presets,
    getPresetNameToDelete,
    setPresetIdToDelete,
  } = useFilterPresets({
    getUrl: parentAttributeListUrl,
    params,
    storageUtils,
    reset: clearRowSelection,
  });

  const [parentAttributeBulkDelete, parentAttributeBulkDeleteOpts] =
    useParentAttributeBulkDeleteMutation({
      onCompleted: data => {
        if (data.attributeBulkDelete?.errors.length === 0) {
          closeModal();
          notify({
            status: "success",
            text: intl.formatMessage({
              id: "parentAttributesDeleted",
              defaultMessage: "Parent attributes successfully deleted",
            }),
          });
          clearRowSelection();
          refetch();
        }
      },
    });

  const [openModal, closeModal] = createDialogActionHandlers<
    ParentAttributeListUrlDialog,
    ParentAttributeListUrlQueryParams
  >(navigate, parentAttributeListUrl, params);

  const [changeFilters, resetFilters, handleSearchChange] = createFilterHandlers({
    cleanupFn: clearRowSelection,
    createUrl: parentAttributeListUrl,
    getFilterQueryParam,
    navigate,
    params,
    keepActiveTab: true,
  });

  const paginationValues = usePaginator({
    pageInfo: data?.attributes?.pageInfo,
    paginationState,
    queryString: params,
  });

  const handleSort = createSortHandler(navigate, parentAttributeListUrl, params);
  const parentAttributes = mapEdgesToItems(data?.attributes) ?? [];

  const handleSelectParentAttributeIds = useCallback(
    (rows: number[], clearSelection: () => void) => {
      if (!parentAttributes) {
        return;
      }

      const rowsIds = rows.map(row => parentAttributes[row]?.id);
      const haveSaveValues = isEqual(rowsIds, selectedRowIds);

      if (!haveSaveValues) {
        setSelectedRowIds(rowsIds);
      }

      setClearDatagridRowSelectionCallback(clearSelection);
    },
    [parentAttributes, selectedRowIds, setClearDatagridRowSelectionCallback, setSelectedRowIds],
  );

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <ParentAttributeListPage
        settings={settings}
        onUpdateListSettings={updateListSettings}
        onFilterPresetsAll={resetFilters}
        onFilterPresetDelete={(id: number) => {
          setPresetIdToDelete(id);
          openModal("delete-search");
        }}
        onFilterPresetPresetSave={() => openModal("save-search")}
        onFilterPresetChange={onPresetChange}
        onFilterPresetUpdate={onPresetUpdate}
        hasPresetsChanged={hasPresetsChanged}
        onParentAttributesDelete={() => openModal("remove")}
        selectedFilterPreset={selectedPreset}
        selectedParentAttributeIds={selectedRowIds}
        filterPresets={presets.map(tab => tab.name)}
        parentAttributes={parentAttributes}
        disabled={loading || parentAttributeBulkDeleteOpts.loading}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        onFilterChange={changeFilters}
        onSearchChange={handleSearchChange}
        onSort={handleSort}
        sort={getSortParams(params)}
        onSelectParentAttributeIds={handleSelectParentAttributeIds}
      />

      <ParentAttributeBulkDeleteDialog
        confirmButtonState={parentAttributeBulkDeleteOpts.status}
        open={params.action === "remove" && selectedRowIds.length > 0}
        onConfirm={async () => {
          await parentAttributeBulkDelete({ variables: { ids: selectedRowIds } });
          clearRowSelection();
        }}
        onClose={closeModal}
        quantity={selectedRowIds.length}
      />

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={onPresetSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={onPresetDelete}
        tabName={getPresetNameToDelete()}
      />
    </PaginatorContext.Provider>
  );
};

ParentAttributeList.displayName = "ParentAttributeList";

export default ParentAttributeList;